import * as React from "react";
import {observer} from "mobx-react";
import type {Report} from "../../../../../../data/models/Report";
import {ButtonV5} from "../../../../button/ButtonV5";
import {StepIndicatorV5} from "../../../../abstract/view/StepIndicatorV5";
import {ReportColumnsV5} from "./pages/columns/ReportColumnsV5";
import {ReportDetailsV5} from "./pages/details/ReportDetailsV5";
import {ReportSortOrderV5} from "./pages/sortorder/ReportSortOrderV5";
import {ReportDisplayNamesV5} from "./pages/displaynames/ReportDisplayNamesV5";
import {ButtonContainerStyled, ReportWizardStyled} from "./ReportWizardV5.style";
import {ReportFilterV5} from "./pages/filters/ReportFilterV5";

interface IReportWizardV5Props {
	readonly report: Report;
	readonly isSaving: boolean;
	readonly isReportView: boolean;
	readonly onClose: (saveChanges: boolean) => void;
	readonly handleSaveClick: (type: "save" | "saveAndRun") => void;
}

interface IReportWizardV5State {
	stepIndex: number;
}

interface IReportWizardPage {
	title: string;
	stepTitle: string;
	component: React.ComponentClass<{report: Report}> & {isValid?(report: Report): boolean};
}

@observer
export class ReportWizardV5 extends React.Component<IReportWizardV5Props, IReportWizardV5State> {
	private readonly _pages: IReportWizardPage[] = [
		{
			title: "Enter report details",
			stepTitle: "Enter details",
			component: ReportDetailsV5,
		},
		{
			title: "Select columns to be included",
			stepTitle: "Select columns",
			component: ReportColumnsV5,
		},
		{
			title: "Sort your columns",
			stepTitle: "Set sort order",
			component: ReportSortOrderV5,
		},
		{
			title: "Filter your data",
			stepTitle: "Apply filters",
			component: ReportFilterV5,
		},
		{
			title: "Customize column names",
			stepTitle: "Customize headings",
			component: ReportDisplayNamesV5,
		},
	].filter((page) => !this.props.isReportView || page.component !== ReportDetailsV5);

	constructor(props: IReportWizardV5Props) {
		super(props);
		this.state = {
			stepIndex: 0,
		};
	}

	private readonly onBackClick = () => {
		const {stepIndex} = this.state;
		const {report, onClose} = this.props;

		if (stepIndex > 0) {
			this.setState({
				stepIndex: stepIndex - 1,
			});
		} else {
			report.cancelEditing();
			onClose(false);
		}
	};

	private readonly onNextClick = () => {
		const {stepIndex} = this.state;

		this.setState({
			stepIndex: stepIndex + 1,
		});
	};

	private readonly onSelectStep = (index: number) => {
		const {stepIndex} = this.state;

		if (stepIndex === 0 && !this._pages[stepIndex].component.isValid(this.props.report)) {
			return;
		}

		this.setState({stepIndex: index});
	};

	public override render() {
		const {report, isReportView, isSaving, handleSaveClick} = this.props;
		const {stepIndex} = this.state;
		const page = this._pages[stepIndex];
		const Component = page.component;
		const isValid = Component.isValid ? Component.isValid(report) : true;
		const hasUnsavedChanges = report.hasUnsavedChanges();

		return (
			<ReportWizardStyled className="ReportWizard">
				<StepIndicatorV5
					steps={["Enter details", "Select columns", "Set sort order", "Apply filters", "Customize headings"].filter(
						(step) => !(isReportView && step === "Enter details"),
					)}
					currentStepIndex={stepIndex}
					onStepClick={this.onSelectStep}
				/>
				<div className="reportPage">
					<Component report={report} />
				</div>
				<ButtonContainerStyled>
					{stepIndex > 0 && (
						<ButtonV5
							className="secondary"
							type="secondary"
							label="Back"
							title="Back"
							onClick={this.onBackClick}
						/>
					)}
					{stepIndex < this._pages.length - 1 && (
						<ButtonV5
							className="secondary"
							type="secondary"
							label="Next"
							title="Next"
							onClick={this.onNextClick}
							disabled={!isValid}
						/>
					)}
					{isReportView ? (
						<ButtonV5
							className="primary"
							label={report.id ? (isSaving ? "Saving..." : "Save") : isSaving ? "Creating" : "Create Report View"}
							title={report.id ? (isSaving ? "Saving..." : "Save") : isSaving ? "Creating" : "Create Report View"}
							onClick={() => handleSaveClick(!report.id ? "saveAndRun" : "save")}
							disabled={!hasUnsavedChanges || !isValid || isSaving}
						/>
					) : (
						<>
							<ButtonV5
								className="primary"
								label={isSaving ? "Saving..." : "Save"}
								title="Save"
								onClick={() => handleSaveClick("save")}
								disabled={!hasUnsavedChanges || !isValid || isSaving}
							/>
							<ButtonV5
								className="primary"
								label={isSaving ? "Saving..." : "Save & Run"}
								title="Save & Run"
								onClick={() => handleSaveClick("saveAndRun")}
								disabled={!hasUnsavedChanges || !isValid || isSaving}
							/>
						</>
					)}
				</ButtonContainerStyled>
			</ReportWizardStyled>
		);
	}
}
